import { z } from "zod";
import { UuidSchema, PlayerNameSchema, MoneySchema, EpochMillisSchema, LocationSchema, } from "./common.js";
import { StatsSchema } from "./stats.js";
/**
 * Profil joueur + sous-données (READ R3, R7-R17, R19).
 * Sources : etheria_profiles/_levels/_ranks/_classes/_pvp_stats/_guild_members, etc.
 */
/** Un métier du joueur : niveau, xp, seuil suivant, quêtes actives. (R12) */
export const PlayerJobSchema = z.object({
    key: z.string(),
    name: z.string(),
    level: z.number().int().min(0),
    xp: z.number().nonnegative(),
    nextThreshold: z.number().nonnegative().nullable(),
    activeQuests: z.number().int().nonnegative().default(0),
});
/** Rang résolu (R10). */
export const PlayerRankSchema = z.object({
    rankId: z.string(),
    display: z.string(),
    weight: z.number().int().optional(),
});
/** Rang gratuit d'aventure, separe du grade boutique/staff. */
export const PlayerAdventureRankSchema = z.object({
    level: z.number().int().min(1).max(50).default(1),
    name: z.string().default("Novice"),
});
/** Classe + profondeur (R11). */
export const PlayerClassSchema = z.object({
    classId: z.string().nullable(),
    name: z.string().nullable(),
    mana: z.number().optional(),
    manaRegen: z.number().optional(),
    bonusHealth: z.number().optional(),
    maitrise: z.number().int().default(0),
    prestige: z.number().int().default(0),
});
/** Économie compacte pour le profil (solde exact + tri). (R8) */
export const PlayerEconomySchema = z.object({
    balance: MoneySchema,
    balanceSort: z.number().int(),
});
/** Compteurs d'activite visibles sur les profils et armureries. */
export const PlayerActivityStatsSchema = z.object({
    blocksWalked: z.number().int().nonnegative().default(0),
    blocksBroken: z.number().int().nonnegative().default(0),
    mobsKilled: z.number().int().nonnegative().default(0),
    fishCaught: z.number().int().nonnegative().default(0),
    cropsHarvested: z.number().int().nonnegative().default(0),
    itemsCrafted: z.number().int().nonnegative().default(0),
    playMinutes: z.number().int().nonnegative().default(0),
});
/** Appartenance de guilde résumée dans le profil. */
export const PlayerGuildRefSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    grade: z.string().nullable(),
});
/**
 * Haut fait / exploit PUBLIC (Armory « feats of strength »).
 *
 * Dérivé côté BFF à partir des statistiques publiques réelles (paliers de kills, niveau réseau,
 * temps de jeu, maîtrises de métiers, fortune, meilleure série, guilde) — aucun stockage dédié requis.
 * `tier` (1-5) pilote la lueur de rareté ; `unlocked` distingue acquis / à débloquer (avec progrès).
 */
export const ProfileAchievementSchema = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    /** Palier de prestige 1-5 (couleur/lueur : bronze→or→arcane). */
    tier: z.number().int().min(1).max(5),
    icon: z.string().default("✦"),
    unlocked: z.boolean(),
    /** Progression 0-1 vers le déblocage (1 si acquis). */
    progress: z.number().min(0).max(1).default(0),
    /** Libellé de progression FR (ex « 340 / 500 éliminations »). */
    progressLabel: z.string().nullable().default(null),
});
/**
 * Titre PUBLIC affichable sous la bannière d'identité (style WoW).
 * `active` marque le titre mis en avant ; les autres sont « débloqués » et collectionnés.
 */
export const ProfileTitleSchema = z.object({
    key: z.string(),
    label: z.string(),
    /** Palier 1-5 pour la teinte (comme les exploits). */
    tier: z.number().int().min(1).max(5).default(1),
    active: z.boolean().default(false),
});
/**
 * Profil complet (R3) : identité, rang, classe, niveau/xp, métiers, économie,
 * tokens, temps de jeu, kills/deaths/streak, guilde.
 */
export const ProfileSchema = z.object({
    uuid: UuidSchema,
    name: PlayerNameSchema,
    online: z.boolean(),
    firstSeen: EpochMillisSchema.nullable(),
    lastSeen: EpochMillisSchema.nullable(),
    playtimeSeconds: z.number().int().nonnegative(),
    rank: PlayerRankSchema,
    adventureRank: PlayerAdventureRankSchema.default({}),
    class: PlayerClassSchema.nullable(),
    /** niveau réseau + xp (R13). */
    level: z.number().int().min(0),
    xp: z.number().nonnegative(),
    nextThreshold: z.number().nonnegative().nullable(),
    jobs: z.array(PlayerJobSchema),
    activity: PlayerActivityStatsSchema.default({}),
    economy: PlayerEconomySchema,
    tokens: z.number().int().nonnegative(),
    kills: z.number().int().nonnegative(),
    deaths: z.number().int().nonnegative(),
    streak: z.number().int().nonnegative(),
    guild: PlayerGuildRefSchema.nullable(),
    /**
     * Titres collectionnés + exploits publics (dérivés BFF). Optionnels pour rétro-compatibilité :
     * un profil sans ces champs reste valide (l'Armory affiche alors une section vide gracieuse).
     */
    titles: z.array(ProfileTitleSchema).default([]),
    achievements: z.array(ProfileAchievementSchema).default([]),
});
/** Statistiques détaillées (R7) : skills, métiers, classe, combat, farming. */
export const PlayerStatsSchema = z.object({
    skills: z.record(z.string(), z.number()),
    jobs: z.array(PlayerJobSchema),
    class: PlayerClassSchema.nullable(),
    combat: z.object({
        kills: z.number().int().nonnegative(),
        deaths: z.number().int().nonnegative(),
        streak: z.number().int().nonnegative(),
        kdr: z.number().nonnegative(),
    }),
    farming: z.record(z.string(), z.number()),
    attributes: StatsSchema,
});
/** Temps de jeu (R17). */
export const PlaytimeSchema = z.object({
    firstSeen: EpochMillisSchema.nullable(),
    lastSeen: EpochMillisSchema.nullable(),
    playtimeSeconds: z.number().int().nonnegative(),
});
/** Une transaction économique (R8, historique). */
export const TransactionSchema = z.object({
    id: z.number().int(),
    ts: EpochMillisSchema,
    type: z.string(),
    amount: MoneySchema,
    balanceAfter: MoneySchema.nullable(),
    reason: z.string().nullable(),
});
/** Économie détaillée (R8). */
export const EconomyDetailSchema = z.object({
    balance: MoneySchema,
    balanceSort: z.number().int(),
    baltopRank: z.number().int().nullable(),
    transactions: z.array(TransactionSchema),
});
/** Home (R15). */
export const HomeSchema = z.object({
    name: z.string(),
    world: z.string(),
    x: z.number(),
    y: z.number(),
    z: z.number(),
    icon: z.string().nullable(),
});
/** Claim (R14). */
export const ClaimSchema = z.object({
    id: z.number().int(),
    world: z.string(),
    chunkX: z.number().int().optional(),
    chunkZ: z.number().int().optional(),
    minX: z.number().int().optional(),
    minZ: z.number().int().optional(),
    maxX: z.number().int().optional(),
    maxZ: z.number().int().optional(),
    name: z.string().nullable(),
    trust: z.array(z.object({ uuid: UuidSchema, name: z.string().nullable(), level: z.string() })),
    teleport: LocationSchema.nullable(),
});
/** Modération (R18) : ban/mute actifs + historique + alts. */
export const PunishmentSchema = z.object({
    id: z.number().int(),
    type: z.string(),
    reason: z.string().nullable(),
    staffName: z.string().nullable(),
    ts: EpochMillisSchema,
    expires: EpochMillisSchema.nullable(),
    active: z.boolean(),
});
export const ModerationRecordSchema = z.object({
    ban: PunishmentSchema.nullable(),
    mute: PunishmentSchema.nullable(),
    history: z.array(PunishmentSchema),
    alts: z.array(z.object({ uuid: UuidSchema, name: z.string().nullable() })),
    identityLinks: z.array(z.string()),
});
//# sourceMappingURL=profile.js.map